import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import usePermissions from '@hooks/usePermissions';
import { makeStyles } from '@mui/styles';
import {
  Box,
  Divider,
  Typography,
  Drawer,
  IconButton,
  Button,
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import { isEmpty, isUndefined } from 'lodash';
import NoteList from '../NoteList/NoteList';

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 380,
    maxWidth: '100%',
  },
  header: {
    padding: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listContainer: {
    padding: 24,
  },
  list: {
    '&::-webkit-scrollbar': {
      width: '4px',
      height: '40px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#DEDEDE',
      borderRadius: '4px',
    },
  },
  listItem: {
    backgroundColor: theme.palette.grey[50],
    marginBottom: 5,
    borderRadius: 4,
    flexDirection: 'column',
    '&.MuiListItem-root': {
      alignItems: 'unset',
    },
  },
  emptyList: {
    padding: 0,
    justifyContent: 'center',
    height: '44px',
    backgroundColor: theme.palette.grey[50],
    fontStyle: 'italic',
    fontSize: '14px',
    color: theme.palette.text.secondary,
  },
}));

const NoteDrawer = ({
  title,
  sessionDetails,
  setShowDrawer,
  showDrawer,
  anonymisedSession,
  setOpenDialog,
  getNotes,
  notes,
  children,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const hasViewOnlyPermission = usePermissions(['see_all_sessions']);
  const preApproveSessionPermission = usePermissions(['pre_approve_session']);

  const readOnlyNotes =
    sessionDetails?.state === 'expired' ||
    sessionDetails?.state === 'rejected' ||
    sessionDetails?.state === 'completed' ||
    sessionDetails?.state === 'aborted' ||
    (!isUndefined(preApproveSessionPermission) &&
      !isUndefined(hasViewOnlyPermission) &&
      sessionDetails?.state !== 'to-be-pre-validated' &&
      sessionDetails?.state !== 'into-pre-validation') ||
    (isUndefined(preApproveSessionPermission) &&
      !isUndefined(hasViewOnlyPermission) &&
      sessionDetails?.state !== 'partially-rejected' &&
      sessionDetails?.state !== 'waiting-room' &&
      sessionDetails?.state !== 'into-validation' &&
      sessionDetails?.state !== 'to-be-validated');

  useEffect(() => {
    if (sessionDetails?.id) getNotes();
  }, [getNotes, sessionDetails?.id]);

  return (
    <Drawer
      anchor="right"
      classes={{ paper: classes.drawer }}
      onClose={() => setShowDrawer(false)}
      open={showDrawer}
      variant="temporary">
      <>
        <Box className={classes.header}>
          <Typography variant="h4">{title}</Typography>
          <IconButton
            style={{ padding: 5 }}
            onClick={() => setShowDrawer(false)}>
            <HighlightOffIcon />
          </IconButton>
        </Box>
        <Divider />
        {children ? (
          children
        ) : (
          <Box
            style={{ overflowY: 'auto' }}
            className={!isEmpty(notes) ? classes.list : ''}>
            <NoteList
              notes={notes}
              anonymisedSession={anonymisedSession}
              listClassName={classes.listContainer}
              emptyListClassName={classes.emptyList}
              listItemClassName={classes.listItem}
            />
          </Box>
        )}
        <Divider />
        {sessionDetails && (
          <Box
            style={{
              padding: 16,
              display: 'flex',
              justifyContent: 'left',
              position: 'sticky',
            }}>
            {!readOnlyNotes && (
              <Button
                color="primary"
                size="small"
                variant="outlined"
                style={{ fontSize: '14px' }}
                startIcon={<AddCommentOutlinedIcon />}
                onClick={() => {
                  setOpenDialog(true);
                }}>
                {t('add_note')}
              </Button>
            )}
          </Box>
        )}
      </>
    </Drawer>
  );
};

export default NoteDrawer;
